import React, { useMemo, useState } from 'react';
import { FileDown, Shield } from 'lucide-react';
import { Button, message } from 'antd';
import { DownloadOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import {
    PropsExcel,
    useCreateExcel,
} from '@/store/salles/CreateExcel/useCreateExcel';
import { useExcelExport } from '@/store/salles/GetExcel/useExcelExport';
import { useAuth } from '@/contexts/Auth';
import { isRecaptchaNotResolved } from '@/contexts/Recaptcha';
import { ContractItem } from './components/CardUploadItem';
import * as S from './styled';

export type ExportReportsProps = {
    params: PropsExcel;
    labelItems?: string;
    title?: string;
    subtitle?: React.ReactNode;
    actionLabel?: string;
    extraActions?: React.ReactNode;
};

const DEFAULT_SUBTITLE = (
    <>
        <Shield size={13} />
        Exportando dados com os filtros ativos
    </>
);

export function ExportReports({
    params,
    labelItems,
    title = 'Exportar relatório',
    subtitle = DEFAULT_SUBTITLE,
    actionLabel = 'Gerar relatório',
    extraActions,
}: ExportReportsProps) {
    const [seeMore, setSeeMore] = useState(false);
    const { data: dataAuth } = useAuth();

    const {
        mutateAsync: mutationCreateExcel,
        isLoading: isCreateExcelLoading,
    } = useCreateExcel();

    const { data: dataExcelExport } = useExcelExport({
        userId: dataAuth?.user?.id,
        type: params.reportType,
        inventoryId: params.inventoryId,
    });

    const exports = useMemo(
        () => dataExcelExport?.data || [],
        [dataExcelExport],
    );

    const onGenerateReport = async () => {
        try {
            await mutationCreateExcel(params);
        } catch (err) {
            if (isRecaptchaNotResolved(err)) return;
            message.error('Erro ao gerar relatório');
        }
    };

    return (
        <S.DownloadProgressContainer>
            <S.DownloadProgressHeader>
                <S.DownloadHeaderInfo>
                    <S.DownloadIconWrapper>
                        <FileDown size={18} />
                    </S.DownloadIconWrapper>
                    <S.DownloadTitleGroup>
                        <S.DownloadTitle>{title}</S.DownloadTitle>
                        <S.DownloadSubtitle>{subtitle}</S.DownloadSubtitle>
                    </S.DownloadTitleGroup>
                </S.DownloadHeaderInfo>
                <S.DownloadHeaderActions>
                    {extraActions}

                    <Button
                        icon={
                            <DownloadOutlined
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                            />
                        }
                        className="green-btn"
                        disabled={isCreateExcelLoading}
                        onClick={onGenerateReport}
                    >
                        {actionLabel}
                    </Button>
                </S.DownloadHeaderActions>
            </S.DownloadProgressHeader>
            <S.DownloadListContent>
                <AnimatePresence>
                    {(seeMore ? exports : exports?.slice(0, 1)).map((item) => (
                        <ContractItem
                            key={item.id}
                            item={item}
                            labelItems={labelItems}
                            shouldAnimate={seeMore}
                        />
                    ))}
                </AnimatePresence>
                <S.DownloadListFooter>
                    <S.DownloadSummary>
                        <S.DownloadCountHighlight>
                            {dataExcelExport?.data?.length || 0}
                        </S.DownloadCountHighlight>
                        relatórios
                    </S.DownloadSummary>
                    {dataExcelExport?.data &&
                        dataExcelExport?.data.length > 1 && (
                            <S.ToggleListButton
                                icon={
                                    seeMore ? (
                                        <UpOutlined
                                            onPointerEnterCapture={undefined}
                                            onPointerLeaveCapture={undefined}
                                        />
                                    ) : (
                                        <DownOutlined
                                            onPointerEnterCapture={undefined}
                                            onPointerLeaveCapture={undefined}
                                        />
                                    )
                                }
                                onClick={() => setSeeMore(!seeMore)}
                            >
                                {seeMore ? 'Ver menos' : 'Ver mais'}
                            </S.ToggleListButton>
                        )}
                </S.DownloadListFooter>
            </S.DownloadListContent>
        </S.DownloadProgressContainer>
    );
}
